/* @jsx h */
import { api } from "@app/data/index";
import { calculateCheckoutTotal } from "@lemmonhq/orders";
import { defineComponent, Ref, h, computed } from "vue";
import { useAuth, useCheckout } from "../../providers";
import { TableCheckoutItem } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";

import { CheckoutTipComponent } from "./CheckoutTipComponent";

export type CheckoutActionsProps = {
  showDiscountModal: Ref<boolean>;
  showProductDiscountEditorSheet: Ref<boolean>;
  showCashModal: Ref<boolean>;
  showCardModal: Ref<boolean>;
  showManualModal: Ref<boolean>;
  showOnlineModal: Ref<boolean>;
  showTerminalModal: Ref<false | "twint" | "card">;
  showPromotionsModal: Ref<boolean>;
  showGiftCardModal: Ref<boolean>;
};

export const getPrintJobForTableBill = async (
  tableId: number,
  items: TableCheckoutItem[] | undefined,
  subtotal: number,
  table: { name: string },
  discount?: any,
  split_payment?: any,
) => {
  // todo: not hardcode it here
  const vatPercentage = 8.1;
  const printJob = await api.getBill(tableId, {
    items,
    payment: {
      table,
      amount: subtotal,
      created_at: new Date().toISOString(),
      tips: 0,
      discount,
      // todo: not hardcode it here
      vat_percentage: vatPercentage,
      vat_amount: (subtotal * vatPercentage) / (1 + vatPercentage),
      split_payment,
    },
  });
  return printJob;
};

export const CheckoutActions = defineComponent<CheckoutActionsProps>({
  name: "CheckoutActions",
  props: [
    "showDiscountModal",
    "showProductDiscountEditorSheet",
    "showCashModal",
    "showCardModal",
    "showTerminalModal",
    "showManualModal",
    "showOnlineModal",
    "showPromotionsModal",
    "showGiftCardModal",
  ],
  setup(props) {
    const checkout = useCheckout();
    const auth = useAuth();
    const { t } = useLocalization();
    const total = computed(() => {
      return checkout.checkout.value
        ? calculateCheckoutTotal(checkout.checkout.value)
        : 0;
    });
    // {"nakka-terminal-v2": true}
    return () => {
      const {
        showCardModal,
        showCashModal,
        showDiscountModal,
        showProductDiscountEditorSheet,
        showTerminalModal,
        showManualModal,
        showGiftCardModal,
      } = props;
      const checkoutData = checkout.checkout.value;
      if (!checkoutData) {
        return null;
      }
      const hasTerminal =
        (auth.user.value?.payment_terminals || []).filter(
          (t) => t.status !== "not_connected",
        ).length === 1;
      const cardPaymentDisabled = total.value <= 0;
      const canUseProductDiscountEditor =
        checkoutData.payload?.type === "cart" ||
        (checkoutData.payload?.type === "table" &&
          (checkoutData.payload.object.type === "full" ||
            checkoutData.payload.object.type === "partial"));

      return (
        <div>
          <div class="mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-4 lg:gap-6 sm:w-[700px]">
            <CheckoutTipComponent />
            <div
              class={`grid gap-2 ${canUseProductDiscountEditor ? "grid-cols-2" : "grid-cols-1"}`}
            >
              <button
                class="sm:w-full btn sm:btn-lg btn-secondary text-1xl lg:text-1xl font-semibold"
                onClick={() => (showDiscountModal.value = true)}
              >
                <span class="material-symbols-outlined mr-1 text-1xl lg:text-1xl">
                  percent
                </span>
                {t("pos.checkout.order_discount_button")}
              </button>
              {canUseProductDiscountEditor ? (
                <button
                  class="sm:w-full btn sm:btn-lg btn-secondary text-1xl lg:text-1xl font-semibold"
                  onClick={() => (showProductDiscountEditorSheet.value = true)}
                >
                  <span class="material-symbols-outlined mr-1 text-1xl lg:text-1xl">
                    local_offer
                  </span>
                  {t("pos.checkout.items_discount_button")}
                </button>
              ) : null}
            </div>
            <button
              class="sm:w-full sm:btn-lg btn btn-accent text-1xl lg:text-1xl font-semibold"
              onClick={() => (props.showPromotionsModal.value = true)}
            >
              <span class="flex justify-items items-center">
                <span class="material-symbols-outlined mr-1 text-1xl lg:text-1xl">
                  social_leaderboard
                </span>{" "}
                Rewards
                {checkout.checkout.value?.promotions &&
                checkout.checkout.value?.promotions.length > 0
                  ? `[${checkout.checkout.value?.promotions.length}]`
                  : ""}
              </span>
            </button>
            <button
              disabled={
                !auth.user.value?.restaurant.payrexx_name ||
                auth.user.value?.restaurant.payrexx_name === "empty" ||
                total.value < 0.5
              }
              class="sm:w-full sm:btn-lg btn btn-neutral text-1xl lg:text-1xl font-semibold"
              style=""
              onClick={() => (props.showOnlineModal.value = true)}
            >
              <span class="flex justify-items items-center">
                <span class="material-symbols-outlined mr-1 text-1xl lg:text-1xl">
                  qr_code_2
                </span>{" "}
                QR Pay
              </span>
            </button>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-4">
              <button
                class="sm:w-full sm:btn-lg btn btn-neutral text-1xl lg:text-1xl font-semibold"
                onClick={() => (showCashModal.value = true)}
              >
                <span class="flex justify-items items-center">
                  <span class="material-symbols-outlined mr-1 text-1xl lg:text-1xl">
                    payments
                  </span>{" "}
                  {t("pos.checkout.cash_button")}
                </span>
              </button>
              <button
                class="sm:w-full sm:btn-lg btn btn-neutral text-1xl lg:text-1xl font-semibold"
                onClick={() => {
                  console.log("showManualModal", showManualModal.value);
                  showManualModal.value = true;
                }}
              >
                <span class="flex justify-items items-center">
                  <span class="material-symbols-outlined mr-1 text-1xl lg:text-2xl">
                    edit_square
                  </span>{" "}
                  {t("pos.checkout.manual_button")}
                </span>
              </button>
            </div>
            {hasTerminal ? (
              <div class="flex flex-col">
                <div class="grid grid-cols-2 gap-4 lg:gap-4">
                  <button
                    disabled={cardPaymentDisabled}
                    onClick={() => (showTerminalModal.value = "card")}
                    class="sm:btn-lg btn btn-primary text-xl font-semibold"
                  >
                    <span class="flex justify-items items-center">
                      <span class="material-symbols-outlined mr-1 text-xl">
                        credit_card
                      </span>{" "}
                      {t("pos.checkout.card_button")}
                    </span>
                  </button>
                  <button
                    disabled={cardPaymentDisabled}
                    onClick={() => (showTerminalModal.value = "twint")}
                    class="sm:btn-lg btn btn-primary text-xl font-semibold"
                  >
                    <span class="flex justify-items items-center">
                      <span class="material-symbols-outlined mr-1 text-xl">
                        hexagon
                      </span>{" "}
                      {t("pos.checkout.twint_button")}
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              <button
                disabled={cardPaymentDisabled}
                onClick={() => (showCardModal.value = true)}
                class="sm:w-full sm:btn-lg btn btn-primary text-1xl lg:text-1xl font-semibold"
              >
                <span class="flex justify-items items-center">
                  <span class="material-symbols-outlined mr-1 text-1xl lg:text-2xl">
                    credit_card
                  </span>{" "}
                  {t("pos.checkout.card_button")}
                </span>
              </button>
            )}
          </div>
        </div>
      );
    };
  },
});
